/**
 * Plan Management API Service
 * CRUD operations for subscription plans
 */

import { adminApiClient } from './api'

export interface PlanFeatures {
  storage_gb_limit: number
  api_access: boolean
  custom_branding: boolean
  priority_support: boolean
  advanced_analytics: boolean
  white_label: boolean
}

export interface PlanUsageStats {
  total_users: number
  active_users: number
  total_organizations: number
  revenue_monthly: number
  revenue_yearly: number
}

export interface SubscriptionPlan {
  id: string
  name: string
  display_name: string
  description?: string
  price_monthly: number
  price_yearly?: number
  messages_limit: number
  team_members_limit: number
  chatbots_limit: number
  storage_limit_mb: number
  teams_limit: number
  features: Record<string, any>
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
  usage_stats?: PlanUsageStats
}

export interface CreatePlanRequest {
  name: string
  display_name: string
  description?: string
  price_monthly: number
  price_yearly?: number
  messages_limit: number
  team_members_limit: number
  chatbots_limit: number
  storage_limit_mb: number
  teams_limit: number
  features?: PlanFeatures
  is_active: boolean
  sort_order: number
}

export interface UpdatePlanRequest {
  name?: string
  display_name?: string
  description?: string
  price_monthly?: number
  price_yearly?: number
  messages_limit?: number
  team_members_limit?: number
  chatbots_limit?: number
  storage_limit_mb?: number
  teams_limit?: number
  features?: PlanFeatures
  is_active?: boolean
  sort_order?: number
}

export interface PlanAnalytics {
  overview: {
    total_plans: number
    active_plans: number
    total_revenue_monthly: number
    total_revenue_yearly: number
  }
  plan_distribution: Record<string, number>
  generated_at: string
}

export const planManagementApi = {
  /**
   * Get all subscription plans
   */
  getAllPlans: async (includeInactive = false, includeStats = false): Promise<SubscriptionPlan[]> => {
    const params = new URLSearchParams()
    if (includeInactive) params.append('include_inactive', 'true')
    if (includeStats) params.append('include_stats', 'true')
    
    const response = await adminApiClient.get(`/admin/plans/?${params.toString()}`)
    return response.data
  },

  /**
   * Get specific subscription plan
   */
  getPlan: async (planId: string, includeStats = true): Promise<SubscriptionPlan> => {
    const params = includeStats ? '?include_stats=true' : ''
    const response = await adminApiClient.get(`/admin/plans/${planId}${params}`)
    return response.data
  },

  /**
   * Create new subscription plan
   */
  createPlan: async (planData: CreatePlanRequest): Promise<SubscriptionPlan> => {
    const response = await adminApiClient.post('/admin/plans/', planData)
    return response.data
  },

  /**
   * Update subscription plan
   */
  updatePlan: async (planId: string, updates: UpdatePlanRequest): Promise<SubscriptionPlan> => {
    const response = await adminApiClient.put(`/admin/plans/${planId}`, updates)
    return response.data
  },

  /**
   * Delete subscription plan
   */
  deletePlan: async (planId: string, force = false): Promise<{ message: string; plan_name: string }> => {
    const params = force ? '?force=true' : ''
    const response = await adminApiClient.delete(`/admin/plans/${planId}${params}`)
    return response.data
  },

  /**
   * Get plan analytics overview
   */
  getPlanAnalytics: async (): Promise<PlanAnalytics> => {
    const response = await adminApiClient.get('/admin/plans/analytics/overview')
    return response.data
  }
}

export default planManagementApi
